<template>
    <v-row
        no-gutters
        class="px-9 pt-4"
    >
        <v-col cols="12">
            <v-row
                no-gutters
                class="mb-2"
            >
                <v-col cols="auto" class="me-auto" align-self="center">
                    <div class="font-weight-medium title">
                        {{ $t('Branding') }}
                    </div>
                </v-col>
            </v-row>
            <v-row
                class="mb-4"
            >
                <v-col cols="6" class="py-0">
                    <v-row
                        no-gutters
                    >
                        <v-col cols="4" class="d-flex">
                            <v-text-field
                                v-model="formData.subdomain"
                                :label="$t('Subdomain') + '*'"
                                outlined
                                :error-messages="formDataErrors.subdomain_error"
                                @input="formDataErrors.subdomain_error = null"
                            />
                        </v-col>
                        <v-col cols="4" class="d-flex ml-2 align-center">
                            .{{ portalDomain }}
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="3" class="py-0 pr-0">
                    <dropdown
                        :items="domainLanguageOptions"
                        :selected="domainLanguageOptions.filter(x => x.code === formData.defaultLanguage)"
                        name-key="text"
                        value-key="code"
                        show-selected-first
                        :error-messages="formDataErrors.default_language_error"
                        :label="$t('Default language')"
                        @input="formData.defaultLanguage = $event.code"
                    />
                </v-col>
                <v-col cols="3" class="py-0 pl-0">
                    <action-menu
                        v-if="currentCopyUrl"
                        :items="domainLanguageOptions"
                        icon="Languages"
                        :tooltip="$t('Client portal URL in other languages')"
                        @action="onCopyUrl(domainLanguageOptions[$event].value, domainLanguageOptions[$event].text)"
                    >
                        <template #activator="{ on, isOpen }">
                            <v-btn depressed text v-on="on" color="primary" class="no-transformers px-2 ml-1" style="margin-top: 23px">
                                <icon size="20" color="primary" icon="Globe" />
                                <div class="mx-2" style="margin-bottom: 1px">{{ $t('Copy URL')}}</div>
                                <icon size="16" color="primary" :icon="isOpen ? 'ChevronUp' : 'ChevronDown'" />
                            </v-btn>
                        </template>
                    </action-menu>
                </v-col>
                <v-col cols="12" class="py-0">
                    <v-row class="mt-1">
                        <v-col cols="6" class="py-0">
                            <v-text-field
                                v-model="formData.trademark"
                                :label="$t('Trademark') + '*'"
                                outlined
                                :error-messages="formDataErrors.trademark_error"
                            />
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="6" class="py-0">
                    <v-row no-gutters>
                        <v-col cols="6">
                            <v-text-field
                                :value="formData.textColor"
                                :label="$t('Trademark text color')"
                                outlined
                                :error-messages="formDataErrors.text_color_error"
                                @change="(val) => handleColorChange('text', val)"
                            />
                        </v-col>
                        <v-col cols="4" class="d-flex align-center">
                            <v-btn depressed text @click="openColorPicker('text')" color="primary" class="no-transformers ml-1 px-2">
                                <v-icon>mdi-palette-outline</v-icon>
                                <div class="mx-2">
                                    {{ $t('Choose color')}}
                                </div>
                            </v-btn>
                            <color-picker v-if="showColorPicker" :show="showColorPicker === 'text'" :swatches="swatches" :color="formData.textColor" :on-select="(color) => pickColor('text', color)" @close="showColorPicker = false"></color-picker>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="6"></v-col>
                <v-col cols="3" class="py-0 pr-0">
                    <v-text-field
                        :value="formData.brandColor"
                        :label="$t('Brand background color')"
                        outlined
                        :error-messages="formDataErrors.brand_color_error"
                        @change="(val) => handleColorChange('brand', val)"
                    />
                </v-col>
                <v-col cols="3" class="py-0 pl-0">
                    <v-btn depressed text @click="openColorPicker('bg')" color="primary" class="no-transformers ml-1 px-2" style="margin-top: 23px">
                        <v-icon>mdi-palette-outline</v-icon>
                        <div class="mx-2">
                            {{ $t('Choose color')}}
                        </div>
                    </v-btn>
                    <color-picker v-if="showColorPicker"  :show="showColorPicker === 'bg'" :swatches="swatches" :color="formData.brandColor" :on-select="(color) => pickColor('bg', color)" @close="showColorPicker = false"></color-picker>
                </v-col>
                <v-col cols="12" class="py-0">
                    <v-row>
                        <v-col cols="6" class="py-0">
                            <div class="d-flex flex-column">
                                <label class="global-label">{{ $t('Logo') }}</label>
                                <v-row v-if="formData.logoUrl">
                                    <v-col cols="auto" class="py-0 pr-0">
                                        <v-img
                                            contain
                                            height="60"
                                            max-width="180"
                                            min-width="60"
                                            :src="formData.logoUrl"
                                        >
                                        </v-img>
                                    </v-col>
                                    <v-col cols="auto" class="pa-0">
                                        <v-btn
                                            class="text-lowercase-first-letter text-none"
                                            text
                                            small
                                            color="error"
                                            @click="removeLogo()"
                                        >
                                            {{ $t('Delete logo') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>
                                <div v-else>
                                    <FileUploadArea
                                        :button-text="$t('Upload logo file')"
                                        :file-upload="logoFileUpload"
                                        :upload-change="uploadChange"
                                        :upload-drop="uploadDrop"
                                    >
                                        <template #help>
                                            <div class="body-2 mt-2 font-weight-medium">
                                                {{ $t("Max. dimensions 180 x 60 px") }}
                                            </div>
                                        </template>
                                    </FileUploadArea>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="6" />
                        <v-col cols="6">
                            <div>
                                <label class="global-label">{{ $t('Icon') }} / {{ $t('Favicon')}}</label>
                                <v-row
                                    no-gutters
                                    v-if="formData.iconUrl"
                                >
                                    <v-col cols="auto">
                                        <img
                                            :alt="$t('Icon')"
                                            style="max-width: 130px;"
                                            :src="formData.iconUrl"
                                        />
                                    </v-col>
                                    <v-col cols="auto">
                                        <v-btn
                                            class="text-lowercase-first-letter text-none"
                                            text
                                            small
                                            color="error"
                                            @click="removeIcon()"
                                        >
                                            {{ $t('Delete icon') }}
                                        </v-btn>
                                    </v-col>
                                </v-row>

                                <div v-else>
                                    <FileUploadArea
                                        :button-text="$t('Upload icon file')"
                                        :file-upload="iconFileUpload"
                                        :upload-change="iconUploadChange"
                                        :upload-drop="iconUploadDrop"
                                    >
                                        <template #help>
                                            <div class="body-2 mt-2 font-weight-medium">
                                                {{ $t("Recommended size 260 x 260 px") }}
                                            </div>
                                        </template>
                                    </FileUploadArea>
                                </div>
                            </div>
                        </v-col>
                        <v-col cols="6" />
                    </v-row>
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-6"
            >
                <v-col cols="12">
                    <div class="font-weight-medium title mb-2">
                        {{ $t('Company description') }}
                    </div>
                    <div class="body-2 g4--text mb-2">
                        {{ $t('Displayed on the login page.') }}
                        {{ $t('Describe here what services you offer and how you help your clients.') }}
                    </div>
                    <tiptap-vuetify
                        v-model="formData.description"
                        output-format="html"
                        :extensions="extensions"
                      />
                </v-col>
            </v-row>
            <v-row
                no-gutters
                class="mb-2"
            >
                <v-col cols="12">
                    <switch-button
                        :value="formData.showVideo"
                        @change="formData.showVideo = $event"
                    >
                        <template #label>
                            <div class="font-weight-medium title">
                                {{ $t('Welcome video') }}
                            </div>
                        </template>
                    </switch-button>

                </v-col>
                <v-col cols="12" class="pl-12">
                    <v-row class="mb-2 ml-0">
                        <div class="body-2 g4--text">
                            {{ $t('Displayed on the login page when your client contact enters for the first time') }}
                        </div>
                    </v-row>
                    <v-row class="ml-0">
                        <v-radio-group
                            v-model="formData.videoOption"
                            :disabled="!formData.showVideo"
                            column
                        >
                            <v-radio
                                value="uku"
                            >
                                <template #label>
                                    {{ $t('Use Uku video') }} -&nbsp;<a @click="openUkuVideo">{{ $t('View') }}</a>
                                </template>
                            </v-radio>
                            <v-radio
                                :label="$t('Use other video')"
                                value="custom"
                            ></v-radio>
                        </v-radio-group>
                    </v-row>
                    <v-row v-if="formData.videoOption !== 'uku'" class="ml-0 move-closer">
                        <v-text-field
                            full-width
                            v-model="formData.welcomeVideoUrl"
                            :disabled="formData.videoOption === 'uku' || !formData.showVideo"
                            :label="$t('Youtube or Vimeo video URL') + (formData.showVideo ? '*': '')"
                            outlined
                            class="mr-2"
                            :error-messages="formDataErrors.welcome_video_url_error"
                            @input="formDataErrors.welcome_video_url_error = null"
                        ></v-text-field>
                    </v-row>
                </v-col>
                <v-col>
                    <switch-button
                        :value="formData.showFileUpload"
                        :label="$t('Show file upload box for client tasks')"
                        @change="formData.showFileUpload = $event"
                    >
                        <template #label>
                            <div class="font-weight-medium title">
                                {{ $t('Show file upload box for client tasks') }}
                            </div>
                        </template>
                    </switch-button>
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
import AppStoreDialogSidebar from "@/components/AppStore/AppStoreDialogSidebar";
import appStoreDialog from "@/store/modules/AppStoreDialog/appStoreDialog";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";
import AppModule from "@/store/modules/app";
import {computed, ref, reactive, watch} from "@vue/composition-api";
import Dialog from "@/components/Dialog";
import { $t } from "@/i18n";
import Loader from "@/utility/loader";
import FileUploadArea from "@/components/CustomField/FileUploadArea";
import FileUpload from "@/composables/FileUpload";
import ApiAppStore from "@/api/app_store/appStore";
import ColorPicker from "@/components/ColorPicker";
import FeatureModule from "@/store/modules/feature";

import { TiptapVuetify, Heading, Bold, Italic, Strike, Underline, BulletList, OrderedList, ListItem, Link, HardBreak, HorizontalRule, History } from 'tiptap-vuetify'
import {formatLanguageLabel} from '@/helpers/formatting'

export default {
    name: "ClientsDialogSettingsTab",
    components: {
        AppStoreDialogSidebar,
        FileUploadArea,
        Dialog,
        ColorPicker,
        TiptapVuetify
    },
    props: {
        isSelected: {
            type: Boolean,
            default: false
        }
    },
    setup(props) {
        /***** INITIAL SETUP *****/
        const {
            appStoreDialogState,
            appStoreDialogFlags,
            appStoreDialogGetters,
            appStoreDialogActions
        } = appStoreDialog()
        const { handleErrorResponse, ui_languages } = AppModule();
        const { showFeedbackNotification } = FeedbackNotificationModule();
        const accountFeatureSettings = appStoreDialogGetters.getCurrentFeatureSettings()

        const savedSubdomain = ref(accountFeatureSettings.data && accountFeatureSettings.data.subdomain ? accountFeatureSettings.data.subdomain : null)
        const getSetting = (settingName, defaultValue=null) => {
            return accountFeatureSettings.data && settingName in accountFeatureSettings.data ? accountFeatureSettings.data[settingName] : defaultValue
        }
        const extensions = [
            History,
            Link,
            Underline,
            Strike,
            Italic,
            ListItem,
            BulletList,
            OrderedList,
            [Heading, {
                options: {
                    levels: [1, 2, 3]
                }
            }],
            Bold,
            HorizontalRule,
            HardBreak
        ]

        const ukuVideoUrl = getSetting('uku_welcome_video', '')
        const portalDomain = getSetting('portal_base_url', 'getuku.com')
        const openUkuVideo = () => window.open(ukuVideoUrl, '_blank')
        /***** INITIAL SETUP *****/


        /***** MANAGE SETTINGS' DATA *****/
        const guessTrademark = () => {
            const accountName = appStoreDialogGetters.getSelectedCompany().name
            let bestGuessTrademark = ''
            if (accountName) {
                bestGuessTrademark = accountName
                const removables = [' OÜ', 'AS ', ' LTD', ' Ltd', ' Inc.', ' Inc']
                removables.forEach((removable) => {
                    if(bestGuessTrademark.endsWith(removable)) {
                        bestGuessTrademark = bestGuessTrademark.replace(removable, '')
                    } else if(bestGuessTrademark.endsWith(removable.toLowerCase())) {
                        bestGuessTrademark = bestGuessTrademark.replace(removable.toLowerCase(), '')
                    }
                })
            }
            return bestGuessTrademark
        }

        const guessSubdomain = () => {
            const accountName = appStoreDialogGetters.getSelectedCompany().name
            let bestGuessTrademark = accountName.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
            bestGuessTrademark = bestGuessTrademark.toLowerCase(); // convert string to lowercase
            bestGuessTrademark = bestGuessTrademark.replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
                       .replace(/\s+/g, '-') // replace spaces with hyphens
                       .replace(/-+/g, '-'); // remove consecutive hyphens
            return bestGuessTrademark
        }

        const getWelcomeVideoValue = () => {
            return formData.value.videoOption === 'uku' ? ukuVideoUrl : formData.value.welcomeVideoUrl
        }

        const formData = ref({
            trademark: getSetting('trademark', guessTrademark()),
            subdomain: getSetting('subdomain', guessSubdomain()),
            description: getSetting('description'),
            logoUrl: getSetting('logo_url', null),
            iconUrl: getSetting('icon_url', null),
            brandColor: getSetting('brand_color', '#FFFFFF'),
            textColor: getSetting('text_color', '#000000'),
            welcomeVideoUrl: getSetting('welcome_video_url', undefined),
            videoOption: !getSetting('welcome_video_url', null) || getSetting('welcome_video_url') === ukuVideoUrl ? 'uku' : 'custom',
            showVideo: getSetting('show_welcome_video', true),
            defaultLanguage: getSetting('default_language', appStoreDialogGetters.getSelectedCompany().locale_code),
            showFileUpload: getSetting('show_file_upload', true)
        })

        const formDataErrors = ref({
            trademark_error: null,
            subdomain_error: null,
            welcome_video_url_error: null,
            text_color_error: null,
            brand_color_error: null,
            default_language_error: null
        })

        const gatherSettings = () => {
            return {
                subdomain: formData.value.subdomain,
                trademark: formData.value.trademark,
                logo_url: formData.value.logoUrl,
                icon_url: formData.value.iconUrl,
                brand_color: formData.value.brandColor,
                text_color: formData.value.textColor,
                welcome_video_url: getWelcomeVideoValue(),
                description: formData.value.description,
                show_welcome_video: formData.value.showVideo,
                default_language: formData.value.defaultLanguage,
                show_file_upload: formData.value.showFileUpload
            }
        }

        /*** MANAGE COLORPICKER ***/

        const swatches = [
            ['#FF0000', '#AA0000', '#550000'],
            ['#00FF00', '#00AA00', '#005500'],
            ['#0000FF', '#0000AA', '#000055']
        ]

        const showColorPicker = ref(false)
        const openColorPicker = (target)  => {
            showColorPicker.value = target
        }

        const pickColor = (target, val) => target === 'text' ? formData.value.textColor = val : formData.value.brandColor = val

        const handleColorChange = (field, val) => {
            if(!isValidColorHex(val) && 0) {
                formDataErrors.value[`${field}_color_error`] = [$t('Invalid data')]
            } else {
                formDataErrors.value[`${field}_color_error`] = null
                formData.value[`${field}Color`] = val
            }
        }

        const isValidColorHex = (val) => /^#[0-9A-F]{6}$/i.test(val)

        /***** /MANAGE SETTINGS' DATA *****/


        /***** LOGO UPLOAD *****/
        const removeLogo = () => formData.value.logoUrl = null
        const addLogo = (file) => formData.value.logoUrl = file.temp_name
        const uploadData = ref({
            files: []
        })
        const relatedObject = ref({
            account_id: appStoreDialogGetters.getAccountIdFromAppData(),
            feature_id: appStoreDialogState.appData.id,
        })
        const logoFileUpload = FileUpload("portal_logo", relatedObject, uploadData, false, addLogo, removeLogo, false);

        const uploadDrop = async function (e) {
            e.preventDefault();
            const files = [...e.dataTransfer.items].map(item => item.getAsFile());
            logoFileUpload.upload_drop({
                dataTransfer: {
                    files: files
                }
            })
        };
        const uploadChange = async (e) => logoFileUpload.upload_change(e)
        /***** LOGO UPLOAD *****/

        /***** ICON UPLOAD *****/
        const removeIcon = () => formData.value.iconUrl = null
        const addIcon = (file) => formData.value.iconUrl = file.temp_name
        const iconUploadData = ref({
            files: []
        })
        const iconRelatedObject = ref({
            account_id: appStoreDialogGetters.getAccountIdFromAppData(),
            feature_id: appStoreDialogState.appData.id,
        })
        const iconFileUpload = FileUpload("portal_icon", iconRelatedObject, iconUploadData, false, addIcon, removeIcon, false);

        const iconUploadDrop = async function (e) {
            e.preventDefault();
            const files = [...e.dataTransfer.items].map(item => item.getAsFile());
            iconFileUpload.upload_drop({
                dataTransfer: {
                    files: files
                }
            })
        };
        const iconUploadChange = async (e) => {
            iconFileUpload.upload_change(e)
        }
        /***** LOGO UPLOAD *****/


        /*****  PREVIEW *****/
        const {execAppAction} = FeatureModule();
        const openPreview = async () => {
            const response = await execAppAction({account_id: appStoreDialogGetters.getSelectedCompany().id, feature_id: appStoreDialogState.appData.id, action: 'preview'})
            if(response && response.url) window.open(response.url, '_blank')
        }

        /***** /PREVIEW *****/
        const showPreview = computed(() => (accountFeatureSettings && accountFeatureSettings.data.subdomain) || saved.value)

        const updateFooterActions = () => {
            if (props.isSelected) {
                const actions = [
                    {
                        text: $t("Save"),
                        class: "primary",
                        click: saveSettings,
                        loading: loading
                    }
                ]

                if (showPreview.value) {
                    actions.unshift({
                        text: $t('Client portal preview'),
                        isTextStyle: true,
                        class: 'primary--text ml-n6 mr-auto',
                        click: openPreview,
                        loading: loading,
                        newIcon: 'ExternalLink'
                    })
                }

                appStoreDialogActions.setFooterActions(actions);
            }
        }


        /*****  SAVE & LOADING *****/
        const saved = ref(false)
        const saveSettings = async () => {
            const data = gatherSettings()
            try {
                await ApiAppStore.saveAppSettings(
                    appStoreDialogGetters.getSelectedCompany().id,
                    appStoreDialogGetters.getCurrentFeature().feature_id,
                    data
                )
                // enable client portal preview
                saved.value = true
                savedSubdomain.value = data.subdomain
                updateFooterActions()
            } catch (e) {
                return handleErrorResponse(formDataErrors.value, e)
            }
            showFeedbackNotification($t('Settings saved'))
        }

        const loader = reactive(new Loader())
        const loading = computed(() => loader.isLoading)

        watch(() => props.isSelected, () => {
            updateFooterActions()
        })
        /*****  /SAVE & LOADING *****/

        const currentCopyUrl = computed(() => {
            if (!savedSubdomain.value) {
                return null
            }

            return `https://${savedSubdomain.value}.${portalDomain}`
        })

        const clientPortalUrl = computed(() => {
            if (!formData.value.subdomain) {
                return null
            }

            return `https://${formData.value.subdomain}.${portalDomain}`
        })

        const domainLanguageOptions = computed(() => {
            const options = []

            for (const language of ui_languages.value) {
                options.push({
                    text: formatLanguageLabel({name: language.name, code: language.code}),
                    value: currentCopyUrl.value + '?locale_code=' + language.code,
                    code: language.code,
                    icon: 'Copy',
                    iconSize: 16,
                    smallIconMargin: true
                })
            }

            return options
        })

        const onCopyUrl = (url, language) => {
            navigator.clipboard.writeText(url)
            showFeedbackNotification($t('Client portal URL for {0} copied to clipboard').format(language))
        }

        return {
            appStoreDialogFlags,
            appStoreDialogActions,
            appStoreDialogGetters,
            appStoreDialogState,
            swatches,
            openColorPicker,
            showColorPicker,
            pickColor,
            handleColorChange,
            portalDomain,
            openUkuVideo,
            loader,
            logoFileUpload,
            removeLogo,
            uploadDrop,
            uploadChange,
            iconFileUpload,
            removeIcon,
            iconUploadChange,
            iconUploadDrop,
            saveSettings,
            formData,
            formDataErrors,
            extensions,
            openPreview,
            showPreview,
            clientPortalUrl,
            domainLanguageOptions,
            onCopyUrl,
            currentCopyUrl,
            savedSubdomain
        }
    }
};
</script>
<style>
.remove-btn {
    cursor: pointer
}
.no-transformers {
    text-transform: none;
}
.move-closer {
    margin-top: -24px;
}
</style>

<style scoped lang="scss">
.tiptap-vuetify-editor {
    ::v-deep {
        h1 {
            font-size: 3.5rem;
            font-weight: 200;
        }

        h2 {
            font-size: 1.5rem;
            font-weight: 600;
        }

        h3 {
            font-size: 1.125rem;
            font-weight: 600;
        }

        h1, h2, h3 {
            margin-top: 16px !important;
            margin-bottom: 8px !important;
        }

        ul li p, ol li p {
            margin-top: 8px !important;
            margin-bottom: 8px !important;
        }
    }
}
</style>